import React, { useEffect, useState, useContext } from "react";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { DeleteConfirm } from "../../../components/modal/deleteConfirm";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "../../../components/button";
import { PageHeading } from "../../../components/pageHeading";
import { Toast } from "../../../components/toast";
import { AuthContext } from "../../../contexts/AuthContext";
import { ShiftDetails } from "./details";
import { Table } from "../../../components/table";
import emptyCalendar from "../../../assets/icons/empty-calendar.svg";

function ShiftTemplates() {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState({
    identifier: "",
    id: "",
  });
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const toggleState = (setState, state) => {
    setState(!state);
  };

  const handleDeleteClick = (identifier, id) => {
    setSelectedDeleteItem({ identifier, id });
    toggleState(setIsDeleteOpen, isDeleteOpen);
  };

  const handleAddMode = (id) => {
    setSelectedTemplateId(id);
    setIsAddMode(false);
    toggleState(setIsDetailsOpen, isDetailsOpen);
  };

  /// Delete template ///
  const deleteTemplate = (templateId) => {
    return authFetch(
      `${process.env.REACT_APP_API_URL}/v1/shift-templates/${templateId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      }
    ).then((response) => response.json());
  };

  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const handleDeleteSuccess = () => {
    fetchTemplates();
    setToast({
      show: true,
      message: "Template has been deleted",
      type: "success",
    });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "" });
  };

  const [fetchFlag, setFetchFlag] = useState(false);

  const handleSuccess = async () => {
    setIsLoading(true);
    setFetchFlag(!fetchFlag);
    // Show success toast for template creation
    setToast({
      show: true,
      message: "Template created successfully",
      type: "success",
    });
  };

  // Fetch templates when orgId changes
  useEffect(() => {
    if (orgId) {
      setIsLoading(true);
      fetchTemplates();
    }
  }, [orgId]);

  // Fetch templates when fetchFlag changes
  useEffect(() => {
    if (orgId) {
      fetchTemplates();
    }
  }, [fetchFlag]);

  /// Fetch templates ///
  const [backendData, setBackendData] = useState([]);
  const fetchTemplates = async () => {
    if (!orgId) return;

    const fetchPromise = authFetch(
      `${process.env.REACT_APP_API_URL}/v1/shift-templates?org_id=${orgId}`
    )
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          setBackendData(data); // Update state with the fetched data
        } else {
          // Handle HTTP errors e.g., 404, 500 etc.
          console.error("HTTP Error: ", response.statusText);
          // Optionally, update state to display an error message
        }
      })
      .catch((error) => {
        // Handle network errors, parsing errors, etc.
        console.error("Fetching error: ", error.message);
        // Optionally, update state to display an error message
      });

    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));

    Promise.all([fetchPromise, timerPromise]).then(() => {
      setIsLoading(false);
    });
  };

  /// Table columns ///
  const columns = React.useMemo(
    () => [
      {
        Header: "Template name",
        accessor: "name",
        // Use a custom cell to render the link
        Cell: ({ row }) => (
          <div>
            <a
              onClick={() => handleAddMode(row.original.id)}
              className="text-gray-800 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:underline capitalize cursor-pointer"
            >
              {row.values.name}
            </a>
          </div>
        ),
      },
      {
        Header: "Time",
        accessor: "start_time",
        Cell: ({ row }) => {
          const startTime = row.original.start_time;
          const endTime = row.original.end_time;

          if (startTime && endTime) {
            const start = new Date(startTime).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hourCycle: "h23",
            });
            const end = new Date(endTime).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hourCycle: "h23",
            });
            return (
              <div className="text-sm text-gray-600">
                {start} - {end}
              </div>
            );
          }
          return <span className="text-gray-400">No time set</span>;
        },
      },
      {
        Header: "Instructions",
        accessor: "instructions",
        Cell: ({ value }) => (
          <div className="max-w-xs truncate" title={value}>
            {value || "No instructions"}
          </div>
        ),
      },
      {
        Header: "Activities",
        accessor: "activities",
        Cell: ({ value }) => (
          <div className="max-w-xs">
            {value && value.length > 0 ? (
              <div className="text-sm text-gray-600">
                {value
                  .filter((activity) => activity && activity.label) // Filter out null activities and activities with null labels
                  .map((activity, index, filteredArray) => (
                    <span key={activity.id}>
                      {activity.label ||
                        `Unknown ${activity.activity_type || "activity"}`}
                      {index < filteredArray.length - 1 ? ", " : ""}
                    </span>
                  ))}
                {value.filter((activity) => activity && !activity.label)
                  .length > 0 && (
                  <span className="text-gray-400">
                    {value.filter((activity) => activity && activity.label)
                      .length > 0
                      ? ", "
                      : ""}
                    {
                      value.filter((activity) => activity && !activity.label)
                        .length
                    }{" "}
                    unknown activity
                    {value.filter((activity) => activity && !activity.label)
                      .length !== 1
                      ? "ies"
                      : ""}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-gray-400">No activities</span>
            )}
          </div>
        ),
      },

      // Add a new column for the delete action
      {
        Header: "",
        id: "delete",
        accessor: (str) => "delete",
        Cell: ({ row }) => (
          <button
            onClick={() =>
              handleDeleteClick(row.original.name, row.original.id)
            }
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-150"
          >
            <TrashIcon className="h-4 w-4 shrink-0" />
          </button>
        ),
      },
    ],
    []
  );

  /// Toggle side sheet ///
  const toggleDetailsOpen = () => {
    if (isDetailsOpen) {
      setSelectedTemplateId(null);
    }
    setIsDetailsOpen(!isDetailsOpen);
  };

  return (
    <div>
      <main className="py-6">
        <div className="px-4 sm:px-6 lg:px-6">
          <PageHeading title="Shift templates">
            <Button
              icon={PlusIcon}
              onClick={() => {
                setIsAddMode(true);
                toggleState(setIsDetailsOpen, isDetailsOpen);
              }}
            >
              Create template
            </Button>
          </PageHeading>

          <Table
            data={backendData}
            columns={columns}
            defaultSort="name"
            isLoading={isLoading}
            emptyState={
              <div className="flex flex-col items-center justify-center h-72">
                <img
                  src={emptyCalendar}
                  alt="Empty calendar"
                  className="text-black w-36 h-36"
                />
                <p className="mt-0 text-gray-300 text-center text-md">
                  No shift templates yet
                </p>
                <Button
                  onClick={() => {
                    setIsAddMode(true);
                    toggleState(setIsDetailsOpen, isDetailsOpen);
                  }}
                >
                  Create template
                </Button>
              </div>
            }
          />
        </div>
      </main>
      <DeleteConfirm
        isOpen={isDeleteOpen}
        toggleOpen={() => toggleState(setIsDeleteOpen, isDeleteOpen)}
        selectedDeleteItem={selectedDeleteItem}
        handleDeleteSuccess={handleDeleteSuccess}
        onDelete={deleteTemplate}
      />
      <ShiftDetails
        isOpen={isDetailsOpen}
        toggleOpen={toggleDetailsOpen}
        selectedShiftId={selectedTemplateId}
        isAddMode={isAddMode}
        onSuccess={handleSuccess}
        isTemplateMode={true}
      />
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={hideToast}
      />
    </div>
  );
}

export { ShiftTemplates };
